import {
  Card,
  DeleteButton,
  Divider,
  notification,
  Space,
  Table,
  TableProps,
  Typography,
} from '@pankod/refine-antd';
import { IPaymentLog, PaginationHandler } from 'interfaces';
import { useEffect, useState } from 'react';
import { getPaymentLogs } from 'services/payment';
import { formatCurrency } from 'utilities/shared';
import { AddPaymentLog } from './AddPaymentLog';
import { EditPaymentLog } from './EditPaymentLog';
import styles from './style.module.css';
import CustomTableWithPagination from 'components/table/custom-table';

type PaymentLogsTableProps = {
  resource: 'sales' | 'purchase';
  resourceId: string;
  paymentBy: 'client' | 'supplier';
  paymentById: string;
  totalPayableAmount: number;
};

interface IPaymentLogState extends IPaymentLog {
  currentPendingAmount: number;
}

const { Title, Text } = Typography;

export const PaymentLogsTable: React.FC<PaymentLogsTableProps> = (props) => {
  const [paymentLogs, setPaymentLogs] = useState<IPaymentLogState[]>([]);
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);

  useEffect(() => {
    async function fetchPaymentLogs() {
      try {
        let currentPendingAmount = props.totalPayableAmount;
        const paymentLogs = await getPaymentLogs(props.resource, props.resourceId);
        const extendedPaymentLogs = paymentLogs.map((paymentLog) => {
          currentPendingAmount -= paymentLog.currentPaidAmount;
          return { ...paymentLog, currentPendingAmount };
        });
        setPaymentLogs(extendedPaymentLogs);
      } catch (err: any) {
        notification.error({
          message: 'Failed',
          description: err.message,
        });
        setPaymentLogs([]);
      }
    }

    fetchPaymentLogs();
  }, [props.resource, props.resourceId, props.totalPayableAmount]);

  const totalCurrentPaidAmount = paymentLogs.reduce((total, log) => {
    total += log.currentPaidAmount;
    return total;
  }, 0);

  const totalPendingAmount = props.totalPayableAmount - totalCurrentPaidAmount;

  const onDeleteSuccessHandler = async () => {
    try {
      let currentPendingAmount = props.totalPayableAmount;
      const paymentLogs = await getPaymentLogs(props.resource, props.resourceId);
      const extendedPaymentLogs = paymentLogs.map((paymentLog) => {
        currentPendingAmount -= paymentLog.currentPaidAmount;
        return { ...paymentLog, currentPendingAmount };
      });

      setPaymentLogs(extendedPaymentLogs);
    } catch (err: any) {
      notification.error({
        message: 'Failed',
        description: err.message,
      });
    }
  };

  const onTableChange: TableProps<any>['onChange'] = async (
    pagination,
    _filters,
    _sorter,
    _extra
  ) => {
    const page = pagination.current ?? 1;
    const limit = pagination.pageSize ?? 10;
    setPage(page);
    setLimit(limit);
  };

  const onPageChange: PaginationHandler = async (page: number, pageSize: number) => {
    setPage(page);
    setLimit(pageSize);
  };

  // Function to calculate the current page data
  const getCurrentPageData = () => {
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;
    return paymentLogs?.slice(startIndex, endIndex);
  };

  const cardTitle = (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Title level={5}>Payment Logs</Title>
      <AddPaymentLog
        resource={props.resource}
        resourceId={props.resourceId}
        paymentBy={props.paymentBy}
        paymentById={props.paymentById}
        totalPendingAmount={totalPendingAmount}
        totalPayableAmount={props.totalPayableAmount}
        setPaymentLogs={setPaymentLogs}
      />
    </div>
  );

  return (
    <Card title={cardTitle}>
      {/* Payment Info */}
      <div className={styles['payment-info']}>
        <div>
          <Title level={5}>Total Invoiced Amount</Title>
          <Text style={{ fontSize: '1.5rem' }}>{formatCurrency(props.totalPayableAmount)}</Text>
        </div>
        <div>
          <Title level={5}>Total Pending Amount</Title>
          <Text style={{ fontSize: '1.5rem' }}>{formatCurrency(totalPendingAmount)}</Text>
        </div>
      </div>

      <Divider />

      {/* Payment Logs */}
      <CustomTableWithPagination
        total={paymentLogs.length || 0}
        page={page}
        limit={limit}
        onPageChange={onPageChange}
      >
        <Table
          // dataSource={paymentLogs}
          dataSource={getCurrentPageData()} // Pass sliced data
          rowKey="id"
          scroll={{ x: true }}
          onChange={onTableChange}
          pagination={false}
        >
          <Table.Column dataIndex="id" title="Receipt ID" />
          <Table.Column<IPaymentLogState>
            dataIndex="paymentDate"
            title="Date"
            render={(_, record): React.ReactNode => {
              const date = new Date(record.paymentDate).toLocaleDateString('en-GB');
              return <Text>{date}</Text>;
            }}
          />
          <Table.Column dataIndex="paymentType" title="Payment Type" />

          {props.resource === 'sales' && (
            <>
              <Table.Column<IPaymentLogState>
                title="Card Number"
                render={(_, record): React.ReactNode => {
                  const data = record.additionalInfo?.cardNo ?? '-';
                  return <Text>{data}</Text>;
                }}
              />
              <Table.Column<IPaymentLogState>
                title="Approval Code"
                render={(_, record): React.ReactNode => {
                  const data = record.additionalInfo?.approvalCode ?? '-';
                  return <Text>{data}</Text>;
                }}
              />
              <Table.Column<IPaymentLogState>
                title="Phone Number"
                render={(_, record): React.ReactNode => {
                  const data = record.additionalInfo?.phoneNo ?? '-';
                  return <Text>{data}</Text>;
                }}
              />
              <Table.Column<IPaymentLogState>
                title="Transaction ID"
                render={(_, record): React.ReactNode => {
                  const data = record.additionalInfo?.transactionId ?? '-';
                  return <Text>{data}</Text>;
                }}
              />
            </>
          )}

          <Table.Column<IPaymentLogState>
            dataIndex="currentPaidAmount"
            title="Paid Amount"
            render={(_, record) => formatCurrency(record.currentPaidAmount)}
          />
          <Table.Column<IPaymentLogState>
            dataIndex="currentPendingAmount"
            title="Pending Amount"
            render={(_, record) => formatCurrency(record.currentPendingAmount)}
          />
          <Table.Column<IPaymentLogState>
            dataIndex="actions"
            title="Actions"
            render={(_, record): React.ReactNode => {
              const resourceName = `${
                props.resource === 'sales' ? 'sale' : 'purchase'
              }PaymentLogCopy`;

              return (
                <Space>
                  <EditPaymentLog
                    resource={props.resource}
                    resourceId={props.resourceId}
                    paymentLog={record}
                    paymentBy={props.paymentBy}
                    paymentById={props.paymentById}
                    totalPayableAmount={props.totalPayableAmount}
                    totalPendingAmount={totalPendingAmount}
                    setPaymentLogs={setPaymentLogs}
                  />
                  <DeleteButton
                    size="small"
                    hideText
                    resourceNameOrRouteName={resourceName}
                    recordItemId={record?.id}
                    onSuccess={onDeleteSuccessHandler}
                  />
                </Space>
              );
            }}
          />
        </Table>
      </CustomTableWithPagination>
    </Card>
  );
};
